//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import {
  Edit,
  TrashCan,
  Checkmark,
  Close,
  Copy,
  CheckmarkFilled,
  WarningAltFilled,
  Locked,
} from '@carbon/icons-react';
import { useTranslation } from 'react-i18next';
import { useUserAuth } from '../../UserAuth';
import { useConfig } from '../../Config';
import { WarningAlt } from '@carbon/icons-react';
import { Dialog, DialogContent } from '../ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '../ui/tooltip';
import CustomSnackbar, { useSnackbar } from '../common/CustomSnackbar';
import MCPDataService, {
  MCPServerSummary,
  MCPServerDetail,
  MCPCreatePayload,
  MCPUpdatePayload,
  IngestJob,
  LOCKED_OOTB_SERVER_NAMES,
} from '../../utils/MCPDataService';
import { useIngestJobsPolling } from './useIngestJobsPolling';
import { phaseLabel, barColorClass, barWidth } from './ingestJobDisplay';
import { notifyIngestionTriggered } from './ingestionTriggerBus';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../ui/table';
import { cn } from '../../lib/utils';
import { isSafeOAuthUrl } from '../../utils/validateUrlUtils';
import {
  maskSensitiveData,
  safeOAuthNavigate,
} from '../../utils/securityUtils';

const DOMAIN_OPTIONS = [
  'SCM',
  'Plan',
  'Build',
  'Test',
  'Deploy',
  'Release',
  'Measure',
  'Others',
] as const;
const CREDENTIAL_TYPE_OPTIONS = ['oauth'] as const;

interface MCPFormData {
  name: string;
  domain: string;
  url: string;
  credential_type: string;
  oauth_client_id: string;
  oauth_client_secret: string;
}

type MCPFormErrors = Partial<Record<keyof MCPFormData, string>>;

const INITIAL_FORM_DATA: MCPFormData = {
  name: '',
  domain: '',
  url: '',
  credential_type: 'oauth',
  oauth_client_id: '',
  oauth_client_secret: '',
};

interface CombinedMCPRow {
  key: string;
  is_ootb: boolean;
  is_enabled: boolean;
  is_locked: boolean;
  name: string;
  credential_type: string;
  domain: string;
  url: string;
  ootb_server?: MCPServerSummary;
  external_connection?: MCPServerSummary;
}

// How long a Status column job indicator stays visible after reaching a
// terminal state before the row reverts to the plain Online/Offline badge.
const TERMINAL_JOB_DISPLAY_MS = 3000;

// Escape hatch: if a server enabled via the toggle never sees its ingest job
// reach a terminal state (e.g. the job never appears in the poll at all —
// backend restart, teamspace mismatch), stop blocking the switch after this
// long and surface an error instead of leaving the control dead forever.
const PENDING_ENABLE_TIMEOUT_MS = 120000;

interface PendingEnable {
  isOotb: boolean;
  teamspaceId: string;
  name: string;
  // Job ids already present for this server at the moment the enable was
  // triggered. A leftover job from a PRIOR run (failed jobs are retained
  // ~300s server-side) must never resolve THIS enable — only a job created
  // after this attempt started counts.
  priorJobIds: Set<string>;
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function MCPTab({ teamspaceId }: { teamspaceId?: string }): JSX.Element {
  const [ootbServers, setOotbServers] = useState<MCPServerSummary[]>([]);
  const [connections, setConnections] = useState<MCPServerSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [connectionToDelete, setConnectionToDelete] =
    useState<MCPServerSummary | null>(null);
  const [editingConnection, setEditingConnection] =
    useState<MCPServerDetail | null>(null);
  const [formData, setFormData] = useState<MCPFormData>({
    ...INITIAL_FORM_DATA,
  });
  const [formErrors, setFormErrors] = useState<MCPFormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isValidating, setIsValidating] = useState<string | null>(null);
  const [isConnectingOauth, setIsConnectingOauth] = useState<string | null>(
    null,
  );
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Servers whose enable-toggle is deferred until their ingest job resolves —
  // see beginEnableFlow/finalizePendingEnable. Keyed by server id.
  const [pendingEnables, setPendingEnables] = useState<
    Map<string, PendingEnable>
  >(new Map());
  const pendingEnableTimeoutsRef = useRef<
    Map<string, ReturnType<typeof setTimeout>>
  >(new Map());
  // Guards finalizePendingEnable against firing twice for the same server
  // across overlapping polls while its async toggle call is in flight.
  const finalizingEnableRef = useRef<Set<string>>(new Set());
  // Job ids whose terminal (completed/failed) Status-column indicator has
  // aged out and should fall back to the plain Online/Offline badge.
  const [hiddenTerminalJobIds, setHiddenTerminalJobIds] = useState<Set<string>>(
    new Set(),
  );
  const terminalHideTimersRef = useRef<
    Map<string, ReturnType<typeof setTimeout>>
  >(new Map());
  const { t } = useTranslation();
  const { isAuthenticated, user } = useUserAuth();
  const { services } = useConfig();
  const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();
  const saveInProgressRef = useRef(false);
  const oauthPendingServerIdRef = useRef<string | null>(null);
  // 'save' = first-time creation, 'toggle' = re-OAuth from toggle, 'connect' = manual re-connect
  const oauthPendingActionRef = useRef<'save' | 'toggle' | 'connect' | null>(
    null,
  );
  // Ref to the currently open OAuth popup — used for popup source validation
  // and to navigate the blank popup after async auth_url is obtained.
  const oauthPopupRef = useRef<Window | null>(null);
  const oauthSuccessHandledRef = useRef(false);
  // Stable ref so the OAuth message listener doesn't need teamspaceId in its dep array
  const teamspaceIdRef = useRef(teamspaceId);

  const userRef = useRef(user);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const showSnackbarRef = useRef(showSnackbar);
  const tRef = useRef(t);

  useEffect(() => {
    userRef.current = user;
    isAuthenticatedRef.current = isAuthenticated;
    showSnackbarRef.current = showSnackbar;
    tRef.current = t;
    teamspaceIdRef.current = teamspaceId;
  }, [user, isAuthenticated, showSnackbar, t, teamspaceId]);

  const mcpDataService = useMemo(
    () =>
      new MCPDataService(
        () => userRef.current,
        () => isAuthenticatedRef.current,
        services?.analyticsai?.url,
      ),
    [services?.analyticsai?.url],
  );

  const handleJobFailed = useCallback((job: IngestJob) => {
    const name = job.server_name || job.target_key;
    // Safe HTML stripping without ReDoS vulnerability
    const stripHtml = (str: string) => str.replace(/<[^<>]*>/g, '');
    const rawError = job.error ? stripHtml(String(job.error)) : '';
    // job.error is often a raw provider exception repr (e.g. "Error code:
    // 429 - {'error': {'message': '...'}}" from OpenAI) rather than a clean
    // sentence. Pull out just the human-readable 'message' field so the
    // toast reads as a sentence instead of a JSON/dict dump.
    // Try a real structured parse first (handles the common case where the
    // embedded object is valid JSON, including messages with commas/quotes
    // that would confuse a regex), falling back to a regex for Python
    // dict-repr payloads (single-quoted, not valid JSON) that JSON.parse
    // can't handle.
    let humanError = '';
    const jsonStart = rawError.indexOf('{');
    const jsonEnd = rawError.lastIndexOf('}');
    if (jsonStart !== -1 && jsonEnd > jsonStart) {
      try {
        const parsed = JSON.parse(rawError.slice(jsonStart, jsonEnd + 1));
        humanError = String(
          parsed?.error?.message ?? parsed?.message ?? '',
        ).trim();
      } catch {
        // Not valid JSON (e.g. a Python dict repr) — fall through to regex.
      }
    }
    if (!humanError) {
      const messageMatch = rawError.match(
        /['"]message['"]\s*:\s*['"](.*?)['"]\s*[,}]/,
      );
      humanError = (messageMatch?.[1] || rawError).trim();
    }
    humanError = humanError.slice(0, 120);
    showSnackbarRef.current(
      humanError
        ? `Ingestion failed for ${name}: ${humanError}`
        : `Ingestion failed for ${name}.`,
      'error',
    );
  }, []);

  const { jobs: ingestJobs } = useIngestJobsPolling(
    mcpDataService,
    teamspaceId,
    handleJobFailed,
  );
  // Read from callbacks that must not depend on ingestJobs directly (that
  // would rebuild every ~3s poll tick and cascade into effects that
  // depend on those callbacks, e.g. re-subscribing the OAuth listener).
  const ingestJobsRef = useRef(ingestJobs);
  useEffect(() => {
    ingestJobsRef.current = ingestJobs;
  }, [ingestJobs]);

  const analyticsOrigin = useMemo(() => {
    try {
      return services?.analyticsai?.url
        ? new URL(services.analyticsai.url).origin
        : '';
    } catch {
      return '';
    }
  }, [services?.analyticsai?.url]);

  const logOauthDebug = useCallback(
    (stage: string, details: Record<string, unknown> = {}) => {
      console.info('[MCPTab OAuth]', {
        stage,
        ...details,
      });
    },
    [],
  );

  const handleCopyUrl = useCallback(
    async (key: string, url: string) => {
      try {
        await navigator.clipboard.writeText(url);
        showSnackbar(t('mcp-url-copied', 'URL copied to clipboard'), 'success');
        setCopiedKey(key);
        if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
        copyTimerRef.current = setTimeout(
          () => setCopiedKey((prev) => (prev === key ? null : prev)),
          2000,
        );
      } catch {
        showSnackbar(t('mcp-copy-failed', 'Failed to copy URL'), 'error');
      }
    },
    [showSnackbar, t],
  );

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  const fetchMCPConnections = useCallback(async () => {
    setIsLoading(true);
    try {
      if (!teamspaceId) {
        setConnections([]);
        setOotbServers([]);
        return;
      }
      const [externalData, ootbData] = await Promise.all([
        mcpDataService.adminListServers(teamspaceId),
        mcpDataService.adminListOotbServers(teamspaceId),
      ]);
      setConnections(externalData);
      setOotbServers(ootbData);
    } catch (error) {
      console.error('Error fetching MCP connections:', error);
      showSnackbarRef.current(
        tRef.current('error-fetching-mcp-connections'),
        'error',
      );
    } finally {
      setIsLoading(false);
    }
  }, [mcpDataService, teamspaceId]);

  const clearPendingEnableTimeout = useCallback((serverId: string) => {
    const timeoutId = pendingEnableTimeoutsRef.current.get(serverId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      pendingEnableTimeoutsRef.current.delete(serverId);
    }
  }, []);

  const clearPendingEnable = useCallback(
    (serverId: string) => {
      clearPendingEnableTimeout(serverId);
      setPendingEnables((prev) => {
        if (!prev.has(serverId)) return prev;
        const next = new Map(prev);
        next.delete(serverId);
        return next;
      });
    },
    [clearPendingEnableTimeout],
  );

  // Resolves a deferred enable once its ingest job reaches a terminal state:
  // on success, persists is_enabled=true via the real toggle endpoint; on
  // failure, the server is simply never turned on. Idempotent against being
  // invoked more than once for the same server across overlapping polls.
  const finalizePendingEnable = useCallback(
    async (serverId: string, pending: PendingEnable, succeeded: boolean) => {
      if (finalizingEnableRef.current.has(serverId)) return;
      finalizingEnableRef.current.add(serverId);
      clearPendingEnable(serverId);

      if (!succeeded) {
        // Ingestion failed — leave the server disabled. The failure toast is
        // already shown once via handleJobFailed/useIngestJobsPolling.
        finalizingEnableRef.current.delete(serverId);
        return;
      }

      try {
        if (pending.isOotb) {
          const response = await mcpDataService.adminToggleOotbServer(
            serverId,
            pending.teamspaceId,
          );
          fetchMCPConnections();
          const statusText = response.toggled.is_enabled
            ? tRef.current('mcp-enabled')
            : tRef.current('mcp-disabled');
          if (response.auto_disabled.length > 0) {
            const names = response.auto_disabled.map((d) => d.name).join(', ');
            showSnackbarRef.current(
              `${capitalize(pending.name)} ${statusText}. ${tRef.current('mcp-auto-disabled')}: ${names}`,
              'success',
            );
          } else {
            showSnackbarRef.current(
              `${capitalize(pending.name)} ${statusText}`,
              'success',
            );
          }
        } else {
          await mcpDataService.adminToggleServer(serverId, {
            teamspace_id: pending.teamspaceId,
          });
          fetchMCPConnections();
          showSnackbarRef.current(
            `${capitalize(pending.name)} ${tRef.current('mcp-enabled')}`,
            'success',
          );
        }
      } catch (err: any) {
        const raw =
          err?.data?.message ||
          err?.data?.error ||
          tRef.current('error-updating-mcp-status');
        const msg = String(raw)
          .replace(/<[^<>]*>/g, '')
          .slice(0, 300);
        showSnackbarRef.current(msg, 'error');
        fetchMCPConnections();
      } finally {
        finalizingEnableRef.current.delete(serverId);
      }
    },
    [clearPendingEnable, fetchMCPConnections, mcpDataService],
  );

  const markPendingEnable = useCallback(
    (
      serverId: string,
      meta: { isOotb: boolean; teamspaceId: string; name: string },
    ) => {
      const pending: PendingEnable = {
        ...meta,
        // Snapshot at trigger time — a job already in this set belongs to a
        // PRIOR attempt (e.g. a failed job still inside its retention
        // window) and must never resolve this new enable.
        priorJobIds: new Set(
          ingestJobsRef.current
            .filter((j) => j.server_id === serverId)
            .map((j) => j.job_id),
        ),
      };
      setPendingEnables((prev) => new Map(prev).set(serverId, pending));
      clearPendingEnableTimeout(serverId);
      const timeoutId = setTimeout(() => {
        if (finalizingEnableRef.current.has(serverId)) return;
        pendingEnableTimeoutsRef.current.delete(serverId);
        setPendingEnables((prev) => {
          if (!prev.has(serverId)) return prev;
          const next = new Map(prev);
          next.delete(serverId);
          return next;
        });
        showSnackbarRef.current(
          `${capitalize(pending.name)}: ${
            tRef.current('mcp-ingestion-timeout') ||
            'Ingestion is taking longer than expected — please check status or try again.'
          }`,
          'error',
        );
      }, PENDING_ENABLE_TIMEOUT_MS);
      pendingEnableTimeoutsRef.current.set(serverId, timeoutId);
    },
    [clearPendingEnableTimeout],
  );

  // Starts the enable flow for a server: triggers ingest and marks it
  // pending, but deliberately does NOT persist is_enabled=true yet. The
  // switch stays off/disabled until the ingest job resolves — see the
  // ingestJobs-watching effect below, which calls finalizePendingEnable.
  const beginEnableFlow = useCallback(
    async (
      serverId: string,
      currentTeamspaceId: string,
      meta: { isOotb: boolean; name: string },
    ) => {
      markPendingEnable(serverId, {
        isOotb: meta.isOotb,
        teamspaceId: currentTeamspaceId,
        name: meta.name,
      });
      try {
        await mcpDataService.catalogIngest(currentTeamspaceId, serverId);
        notifyIngestionTriggered(currentTeamspaceId);
      } catch (err: any) {
        clearPendingEnable(serverId);
        const raw =
          err?.data?.message ||
          err?.data?.error ||
          tRef.current('error-updating-mcp-status');
        const msg = String(raw)
          .replace(/<[^<>]*>/g, '')
          .slice(0, 300);
        showSnackbarRef.current(msg, 'error');
      }
    },
    [clearPendingEnable, markPendingEnable, mcpDataService],
  );

  // Watches ingestJobs (unfiltered — never the terminal-hide-filtered view
  // used for Status column rendering) for jobs belonging to a pending-enable
  // server reaching a terminal state, and resolves the deferred enable.
  // Excludes priorJobIds so a stale job from an earlier attempt (failed jobs
  // are retained ~300s server-side) can never resolve a NEW enable attempt.
  useEffect(() => {
    pendingEnables.forEach((pending, serverId) => {
      // Prefer the newest post-snapshot job for this server — backend
      // ordering isn't guaranteed, so if more than one new job ever exists
      // for the same server this stays deterministic (see the identical
      // fix applied to the Status column's job lookup).
      const job = ingestJobs
        .filter(
          (j) => j.server_id === serverId && !pending.priorJobIds.has(j.job_id),
        )
        .sort((a, b) => b.created_at - a.created_at)[0];
      if (!job) return;
      if (job.status === 'completed') {
        void finalizePendingEnable(serverId, pending, true);
      } else if (job.status === 'failed') {
        void finalizePendingEnable(serverId, pending, false);
      }
    });
  }, [ingestJobs, pendingEnables, finalizePendingEnable]);

  // Watches ingestJobs for ANY job reaching a terminal state and refreshes
  // connections from the backend — independent of pendingEnables. Needed
  // because backend-driven state changes (e.g. the legacy bulk /api/ingest
  // trigger fired when an AI integration is added/reconnected, which
  // enables/disables OOTB servers server-side based on that job's outcome)
  // must be reflected immediately, even for servers the user never toggled
  // themselves. Skips servers currently in pendingEnables/finalizingEnableRef
  // — those are already refetched by finalizePendingEnable, and racing a
  // second fetch against it (against a stale is_enabled snapshot) could
  // flip the row back to its pre-enable state.
  const refetchedTerminalJobIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    let shouldRefetch = false;
    ingestJobs.forEach((job) => {
      const isTerminal = job.status === 'completed' || job.status === 'failed';
      if (!isTerminal) return;
      if (refetchedTerminalJobIdsRef.current.has(job.job_id)) return;
      if (
        job.server_id &&
        (pendingEnables.has(job.server_id) ||
          finalizingEnableRef.current.has(job.server_id))
      ) {
        return;
      }
      refetchedTerminalJobIdsRef.current.add(job.job_id);
      shouldRefetch = true;
    });
    if (shouldRefetch) {
      fetchMCPConnections();
    }
  }, [ingestJobs, pendingEnables, fetchMCPConnections]);

  // Status column terminal-state display timer (issue: failed/completed
  // jobs must not linger in the Status cell for the backend's full
  // retention window — only ~3s after reaching a terminal state).
  useEffect(() => {
    ingestJobs.forEach((job) => {
      const isTerminal = job.status === 'completed' || job.status === 'failed';
      if (!isTerminal) return;
      if (hiddenTerminalJobIds.has(job.job_id)) return;
      if (terminalHideTimersRef.current.has(job.job_id)) return;
      const timerId = setTimeout(() => {
        terminalHideTimersRef.current.delete(job.job_id);
        setHiddenTerminalJobIds((prev) => new Set(prev).add(job.job_id));
      }, TERMINAL_JOB_DISPLAY_MS);
      terminalHideTimersRef.current.set(job.job_id, timerId);
    });
  }, [ingestJobs, hiddenTerminalJobIds]);

  // Prune hidden-job and refetched-job bookkeeping once a job drops out of
  // the poll entirely (backend's own retention window expired) to keep both
  // sets bounded instead of growing for the component's whole lifetime.
  useEffect(() => {
    const activeIds = new Set(ingestJobs.map((j) => j.job_id));
    refetchedTerminalJobIdsRef.current.forEach((id) => {
      if (!activeIds.has(id)) {
        refetchedTerminalJobIdsRef.current.delete(id);
      }
    });
    setHiddenTerminalJobIds((prev) => {
      let changed = false;
      const next = new Set<string>();
      prev.forEach((id) => {
        if (activeIds.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [ingestJobs]);

  useEffect(() => {
    const pendingTimeouts = pendingEnableTimeoutsRef.current;
    const hideTimers = terminalHideTimersRef.current;
    return () => {
      pendingTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      pendingTimeouts.clear();
      hideTimers.forEach((timeoutId) => clearTimeout(timeoutId));
      hideTimers.clear();
    };
  }, []);

  const completeOauthEnablement = useCallback(
    async (pendingId: string, pendingAction: 'save' | 'toggle') => {
      const currentTeamspaceId = teamspaceIdRef.current;
      if (!currentTeamspaceId) {
        return;
      }

      if (pendingAction === 'toggle') {
        logOauthDebug('post_oauth_ingest_start', {
          pendingAction,
          pendingServerId: pendingId,
          teamspaceId: currentTeamspaceId,
        });
      }

      const connectionMeta = connections.find((c) => c.id === pendingId);
      await beginEnableFlow(pendingId, currentTeamspaceId, {
        isOotb: false,
        name: connectionMeta?.name || pendingId,
      });

      showSnackbarRef.current(
        tRef.current('mcp-oauth-connected-ingest-started') ||
          'OAuth connected — ingestion started',
        'success',
      );
    },
    [beginEnableFlow, connections, logOauthDebug],
  );

  useEffect(() => {
    fetchMCPConnections();
  }, [fetchMCPConnections]);

  // Listen for OAuth popup result — handles save (first-time), toggle (re-OAuth), and connect flows
  useEffect(() => {
    const handleOAuthMessage = async (event: MessageEvent) => {
      const allowedOrigins = new Set(
        [window.location.origin, analyticsOrigin].filter(Boolean),
      );
      const sourceMatchesTrackedPopup = oauthPopupRef.current
        ? event.source === oauthPopupRef.current
        : null;
      logOauthDebug('message_received', {
        data: event.data,
        origin: event.origin,
        allowedOrigins: Array.from(allowedOrigins),
        hasTrackedPopup: Boolean(oauthPopupRef.current),
        sourceMatchesTrackedPopup,
        pendingAction: oauthPendingActionRef.current,
        pendingServerId: oauthPendingServerIdRef.current,
        teamspaceId: teamspaceIdRef.current,
      });
      if (!allowedOrigins.has(event.origin)) {
        logOauthDebug('message_rejected_origin', {
          data: event.data,
          origin: event.origin,
          allowedOrigins: Array.from(allowedOrigins),
        });
        return;
      }
      if (oauthPopupRef.current && event.source !== oauthPopupRef.current) {
        logOauthDebug('message_source_mismatch_accepted', {
          data: event.data,
          origin: event.origin,
          pendingAction: oauthPendingActionRef.current,
          pendingServerId: oauthPendingServerIdRef.current,
          teamspaceId: teamspaceIdRef.current,
        });
      }

      if (event.data === 'mcp_connected') {
        if (oauthSuccessHandledRef.current) {
          return;
        }
        oauthSuccessHandledRef.current = true;
        const pendingId = oauthPendingServerIdRef.current;
        const pendingAction = oauthPendingActionRef.current;
        logOauthDebug('message_connected_accepted', {
          pendingAction,
          pendingServerId: pendingId,
          teamspaceId: teamspaceIdRef.current,
        });
        oauthPendingServerIdRef.current = null;
        oauthPendingActionRef.current = null;
        oauthPopupRef.current = null;
        if (pendingAction === 'connect') {
          // The checkmark "Connect OAuth" button is a manual re-check/refresh
          // of an existing connector's credential and can be used while the
          // connector is already enabled. It must never run the enable/ingest
          // flow: completeOauthEnablement ends in a blind PATCH .../toggle,
          // which would flip an already-enabled server OFF instead of
          // leaving its state untouched (NEXUS00006309).
          logOauthDebug('post_oauth_manual_reconnect', {
            pendingAction,
            pendingServerId: pendingId,
            teamspaceId: teamspaceIdRef.current,
          });
          showSnackbarRef.current(
            tRef.current('mcp-oauth-reconnected-successfully') ||
              'OAuth credentials refreshed',
            'success',
          );
        } else if (pendingId && pendingAction) {
          void completeOauthEnablement(pendingId, pendingAction);
        } else {
          // 'save' always sets pendingId alongside pendingAction (see the
          // auto-trigger in handleSave), so it is always caught by the
          // branch above; this is the fallback for the rare case where
          // pendingId/pendingAction is otherwise missing.
          logOauthDebug('post_oauth_no_stateful_followup', {
            pendingAction,
            pendingServerId: pendingId,
            teamspaceId: teamspaceIdRef.current,
          });
          showSnackbarRef.current(
            tRef.current('mcp-oauth-connected-successfully') ||
              'OAuth re-connected successfully',
            'success',
          );
        }
      } else if (event.data === 'mcp_oauth_failed') {
        const pendingId = oauthPendingServerIdRef.current;
        const pendingAction = oauthPendingActionRef.current;
        logOauthDebug('message_failed_received', {
          pendingAction,
          pendingServerId: pendingId,
          teamspaceId: teamspaceIdRef.current,
        });
        oauthPendingServerIdRef.current = null;
        oauthPendingActionRef.current = null;
        oauthPopupRef.current = null;

        showSnackbarRef.current(
          tRef.current('mcp-oauth-connect-failed') ||
            'OAuth authorization failed',
          'error',
        );
        fetchMCPConnections();
      }
    };
    window.addEventListener('message', handleOAuthMessage);

    return () => {
      window.removeEventListener('message', handleOAuthMessage);
    };
  }, [
    analyticsOrigin,
    completeOauthEnablement,
    fetchMCPConnections,
    logOauthDebug,
  ]);

  useEffect(() => {
    const handleOAuthFallback = async () => {
      const pendingId = oauthPendingServerIdRef.current;
      const pendingAction = oauthPendingActionRef.current;

      if (!pendingId || !pendingAction) {
        return;
      }

      if (pendingAction !== 'save' && pendingAction !== 'connect') {
        return;
      }

      if (oauthSuccessHandledRef.current) {
        return;
      }

      const popup = oauthPopupRef.current;
      if (popup && !popup.closed) {
        return;
      }

      const teamspaceId = teamspaceIdRef.current;
      if (!teamspaceId) {
        return;
      }

      try {
        const status = await mcpDataService.adminGetOauthStatus(
          pendingId,
          teamspaceId,
        );

        if (!status.has_stored_credential || status.oauth_required) {
          return;
        }

        oauthSuccessHandledRef.current = true;
        oauthPendingServerIdRef.current = null;
        oauthPendingActionRef.current = null;
        oauthPopupRef.current = null;

        if (pendingAction === 'connect') {
          // See the mcp_connected handler above: 'connect' must never run
          // the enable/ingest flow, which would blindly toggle an
          // already-enabled connector off (NEXUS00006309).
          showSnackbarRef.current(
            tRef.current('mcp-oauth-reconnected-successfully') ||
              'OAuth credentials refreshed',
            'success',
          );
        } else {
          void completeOauthEnablement(pendingId, pendingAction);
        }
      } catch {
        // Fallback is best-effort only; normal postMessage flow remains primary.
      }
    };

    const handleWindowFocus = () => {
      void handleOAuthFallback();
    };

    window.addEventListener('focus', handleWindowFocus);
    return () => window.removeEventListener('focus', handleWindowFocus);
  }, [completeOauthEnablement, mcpDataService]);

  const handleOotbToggle = async (server: MCPServerSummary) => {
    if (
      server.is_enabled &&
      LOCKED_OOTB_SERVER_NAMES.has(server.name.trim().toLowerCase())
    ) {
      // Defense in depth — only the disable direction is guarded (the
      // Switch is already disabled in that state, see combinedConnections/
      // is_locked below); a locked server that's somehow off must stay
      // re-enableable, so the enable direction always falls through.
      return;
    }
    const newEnabled = !server.is_enabled;

    if (newEnabled) {
      // Enable path: defer the actual toggle-on until ingestion resolves —
      // see beginEnableFlow. The switch stays off/disabled in the meantime.
      if (!teamspaceId) {
        showSnackbar(t('error-updating-mcp-status'), 'error');
        return;
      }
      await beginEnableFlow(server.id, teamspaceId, {
        isOotb: true,
        name: server.name,
      });
      return;
    }

    // Disable path: unchanged, synchronous cleanup-then-toggle.
    const previousOotb = [...ootbServers];
    const previousConnections = [...connections];
    setOotbServers((prev) =>
      prev.map((s) => (s.id === server.id ? { ...s, is_enabled: false } : s)),
    );

    try {
      if (teamspaceId) {
        await mcpDataService.catalogCleanup(teamspaceId, server.id);
      }
      await mcpDataService.adminToggleOotbServer(server.id, teamspaceId);
      fetchMCPConnections();
      showSnackbar(
        `${capitalize(server.name)} ${t('mcp-disabled')}`,
        'success',
      );
    } catch (error: any) {
      console.error('OOTB MCP toggle failed:', server.name, error);

      setOotbServers(previousOotb);
      setConnections(previousConnections);

      const rawErrMsg =
        error?.data?.message ||
        error?.data?.error ||
        t('ootb-mcp-toggle-error');
      // Sanitize: strip HTML tags and cap length to prevent XSS via backend messages
      const errMsg = String(rawErrMsg)
        .replace(/<[^<>]*>/g, '')
        .slice(0, 300);
      showSnackbar(errMsg, 'error');
    }
  };

  const handleToggle = async (connection: MCPServerSummary) => {
    const isEnabling = !connection.is_enabled;

    if (!isEnabling) {
      // Disable path: unchanged, synchronous cleanup-then-toggle.
      const previousConnections = [...connections];
      setConnections((prev) =>
        prev.map((c) =>
          c.id === connection.id ? { ...c, is_enabled: false } : c,
        ),
      );
      try {
        const body: Record<string, string> = teamspaceId
          ? { teamspace_id: teamspaceId }
          : {};
        if (teamspaceId) {
          await mcpDataService.catalogCleanup(teamspaceId, connection.id);
        }
        await mcpDataService.adminToggleServer(connection.id, body);
        fetchMCPConnections();
      } catch (error: any) {
        setConnections(previousConnections);
        const rawMsg =
          error?.data?.message ||
          error?.data?.error ||
          t('error-updating-mcp-status');
        // Sanitize: strip HTML tags and cap length to prevent XSS via backend messages
        const msg = String(rawMsg)
          .replace(/<[^<>]*>/g, '')
          .slice(0, 300);
        showSnackbar(msg, 'error');
      }
      return;
    }

    // Enable path: OAuth-gated connections must authorize first; otherwise
    // defer the actual toggle-on until ingestion resolves — see
    // beginEnableFlow. The switch stays off/disabled in the meantime.
    if (!teamspaceId) {
      showSnackbar(
        t('teamspace-required-for-oauth') ||
          'Teamspace context required for OAuth connect',
        'error',
      );
      return;
    }

    // Pre-open blank popup synchronously for OAuth MCPs being enabled.
    // If OAuth is required, we navigate this popup instead of calling
    // window.open after async work (which browsers block).
    const isOAuthConnection = connection.credential_type === 'oauth';
    let toggleOauthPopup: Window | null = null;
    if (isOAuthConnection) {
      toggleOauthPopup = window.open('', '_blank', 'width=600,height=700');
    }

    try {
      if (isOAuthConnection) {
        const oauthStatus = await mcpDataService.adminGetOauthStatus(
          connection.id,
          teamspaceId,
        );
        if (oauthStatus.oauth_required) {
          logOauthDebug('toggle_requires_oauth', {
            pendingServerId: connection.id,
            teamspaceId,
          });
          oauthSuccessHandledRef.current = false;
          oauthPendingServerIdRef.current = connection.id;
          oauthPendingActionRef.current = 'toggle';
          const resp = await mcpDataService.adminConnectOauth(
            connection.id,
            teamspaceId,
          );
          if (
            'auth_url' in resp &&
            resp.auth_url &&
            isSafeOAuthUrl(resp.auth_url)
          ) {
            // Break taint chain: reconstruct from validated URL components (CWE-79)
            const parsedAuthUrl = new URL(resp.auth_url);
            const sanitizedAuthUrl = `${parsedAuthUrl.origin}${parsedAuthUrl.pathname}${parsedAuthUrl.search}${parsedAuthUrl.hash}`;
            if (toggleOauthPopup && !toggleOauthPopup.closed) {
              if (!safeOAuthNavigate(toggleOauthPopup, resp.auth_url)) {
                toggleOauthPopup.close();
                showSnackbar(
                  t('mcp-invalid-auth-url', 'Invalid authentication URL'),
                  'error',
                );
                return;
              }
              oauthPopupRef.current = toggleOauthPopup;
            } else {
              oauthPopupRef.current = window.open(
                sanitizedAuthUrl,
                '_blank',
                'noopener,noreferrer,width=600,height=700',
              );
            }
            logOauthDebug('popup_opened_toggle', {
              pendingServerId: connection.id,
              teamspaceId,
              authUrl: sanitizedAuthUrl,
              popupTracked: Boolean(oauthPopupRef.current),
            });
            showSnackbar(
              t(
                'mcp-oauth-window-opened',
                'OAuth authorization required — complete in the popup to enable',
              ),
              'warning',
            );
            return;
          } else {
            showSnackbar(
              t('mcp-invalid-auth-url', 'Invalid authentication URL'),
              'error',
            );
            return;
          }
        }
      }

      await beginEnableFlow(connection.id, teamspaceId, {
        isOotb: false,
        name: connection.name,
      });
      if (toggleOauthPopup && !toggleOauthPopup.closed)
        toggleOauthPopup.close();
    } catch (error: any) {
      if (toggleOauthPopup && !toggleOauthPopup.closed)
        toggleOauthPopup.close();
      const rawMsg =
        error?.data?.message ||
        error?.data?.error ||
        t('error-updating-mcp-status');
      // Sanitize: strip HTML tags and cap length to prevent XSS via backend messages
      const msg = String(rawMsg)
        .replace(/<[^<>]*>/g, '')
        .slice(0, 300);
      showSnackbar(msg, 'error');
    }
  };

  const handleAdminConnectOauth = async (connection: MCPServerSummary) => {
    if (!teamspaceId) {
      showSnackbar(
        t('teamspace-required-for-oauth') ||
          'Teamspace context required for OAuth connect',
        'error',
      );
      return;
    }
    setIsConnectingOauth(connection.id);
    oauthSuccessHandledRef.current = false;
    // Open blank popup synchronously to avoid popup blocker.
    // location.href is assigned after obtaining auth_url from the async call.
    const popup = window.open('', '_blank', 'width=600,height=700');
    oauthPopupRef.current = popup;
    // Track which server triggered OAuth so mcp_connected can refresh token
    oauthPendingServerIdRef.current = connection.id;
    oauthPendingActionRef.current = 'connect';
    logOauthDebug('popup_opened_connect_blank', {
      pendingServerId: connection.id,
      teamspaceId,
      popupTracked: Boolean(popup),
    });
    try {
      const resp = await mcpDataService.adminConnectOauth(
        connection.id,
        teamspaceId,
      );
      if (
        'auth_url' in resp &&
        resp.auth_url &&
        isSafeOAuthUrl(resp.auth_url)
      ) {
        // Break taint chain: reconstruct from validated URL components (CWE-79)
        const parsedConnectUrl = new URL(resp.auth_url);
        const sanitizedConnectUrl = `${parsedConnectUrl.origin}${parsedConnectUrl.pathname}${parsedConnectUrl.search}${parsedConnectUrl.hash}`;
        if (popup && !popup.closed) {
          if (!safeOAuthNavigate(popup, resp.auth_url)) {
            popup.close();
            showSnackbar(
              t('mcp-invalid-auth-url', 'Invalid authentication URL'),
              'error',
            );
            return;
          }
        } else {
          oauthPopupRef.current = window.open(
            sanitizedConnectUrl,
            '_blank',
            'noopener,noreferrer,width=600,height=700',
          );
        }
        logOauthDebug('popup_opened_connect_auth', {
          pendingServerId: connection.id,
          teamspaceId,
          authUrl: sanitizedConnectUrl,
          popupTracked: Boolean(oauthPopupRef.current || popup),
        });
        showSnackbar(
          t(
            'mcp-oauth-window-opened',
            'OAuth window opened — complete authorization to connect',
          ),
          'success',
        );
      } else {
        showSnackbar(
          t('mcp-invalid-auth-url', 'Invalid authentication URL'),
          'error',
        );
      }
    } catch (error: any) {
      if (popup && !popup.closed) popup.close();
      oauthPopupRef.current = null;
      oauthPendingServerIdRef.current = null;
      oauthPendingActionRef.current = null;
      const raw =
        error?.data?.message ||
        error?.data?.error ||
        t('mcp-oauth-connect-failed', 'OAuth connect failed');
      // Sanitize: strip HTML tags and cap length to prevent XSS via backend messages
      const msg = String(raw)
        .replace(/<[^<>]*>/g, '')
        .slice(0, 300);
      showSnackbar(msg, 'error');
    } finally {
      setIsConnectingOauth(null);
    }
  };

  const handleValidate = async (connection: MCPServerSummary) => {
    if (!teamspaceId) {
      showSnackbar(t('mcp-validation-failed'), 'error');
      return;
    }
    setIsValidating(connection.id);
    try {
      const response = await mcpDataService.adminValidateServer(
        connection.id,
        teamspaceId,
      );
      if (response.status === 'Online') {
        showSnackbar(
          `${connection.name}: ${t('mcp-validated-successfully')}` +
            (response.latency_ms ? ` (${response.latency_ms}ms)` : ''),
          'success',
        );
      } else {
        showSnackbar(response.error || t('mcp-validation-failed'), 'error');
      }
    } catch (error) {
      showSnackbar(t('mcp-validation-failed'), 'error');
    } finally {
      setIsValidating(null);
    }
  };

  const openAddModal = () => {
    setEditingConnection(null);
    setFormData({ ...INITIAL_FORM_DATA });
    setFormErrors({});
    setIsPanelOpen(true);
  };

  const openEditModal = async (connection: MCPServerSummary) => {
    if (!teamspaceId) {
      showSnackbar(t('error-fetching-mcp-connections'), 'error');
      return;
    }
    try {
      const detail: MCPServerDetail = await mcpDataService.adminGetServer(
        connection.id,
        teamspaceId,
      );
      setEditingConnection(detail);
      setFormData({
        name: detail.name,
        domain: (DOMAIN_OPTIONS as readonly string[]).includes(detail.domain)
          ? detail.domain
          : 'Others',
        url: detail.url,
        credential_type: detail.credential_type,
        oauth_client_id: detail.oauth_client_id || '',
        oauth_client_secret: '',
      });
      setFormErrors({});
      setIsPanelOpen(true);
    } catch (error) {
      showSnackbar(t('error-fetching-mcp-connections'), 'error');
    }
  };

  const closePanel = () => {
    if (saveInProgressRef.current) return;
    setIsPanelOpen(false);
    setEditingConnection(null);
    setFormData({ ...INITIAL_FORM_DATA });
    setFormErrors({});
  };

  const isValidUrl = (value: string): boolean => {
    try {
      const u = new URL(value);
      return u.protocol === 'https:' || u.protocol === 'http:';
    } catch {
      return false;
    }
  };

  const validateForm = (): boolean => {
    const errors: MCPFormErrors = {};

    if (!formData.name.trim()) {
      errors.name = t('name-is-required');
    } else if (!/^[a-zA-Z0-9 _-]*$/.test(formData.name.trim())) {
      errors.name = t('specialCharactersNotAllowed');
    } else if (!editingConnection) {
      const isDuplicate = connections.some(
        (c) => c.name.toLowerCase() === formData.name.trim().toLowerCase(),
      );
      if (isDuplicate) {
        errors.name = t('name-must-be-unique');
      }
    } else if (editingConnection) {
      const isDuplicate = connections.some(
        (c) =>
          c.id !== editingConnection.id &&
          c.name.toLowerCase() === formData.name.trim().toLowerCase(),
      );
      if (isDuplicate) {
        errors.name = t('name-must-be-unique');
      }
    }

    if (!formData.domain) {
      errors.domain = t('domain-is-required');
    } else {
      // Only one active MCP per domain — frontend pre-check (backend also enforces)
      const activeSameDomain = [...connections, ...ootbServers].filter(
        (c) =>
          c.domain === formData.domain &&
          c.is_enabled &&
          (!editingConnection || c.id !== editingConnection.id),
      );
      if (activeSameDomain.length > 0) {
        errors.domain =
          t('domain-already-has-active-mcp') ||
          `Domain '${formData.domain}' already has an active MCP: ${activeSameDomain[0].name}`;
      }
    }

    if (!formData.url.trim()) {
      errors.url = t('mcp-url-required');
    } else if (!isValidUrl(formData.url.trim())) {
      errors.url = t('mcp-url-invalid');
    }

    setFormErrors(errors);

    // Scroll to first error field so the user sees the validation message
    if (Object.keys(errors).length > 0) {
      const firstErrorKey = Object.keys(errors)[0];
      const fieldMap: Record<string, string> = {
        name: 'mcp-name',
        domain: 'mcp-domain',
        url: 'mcp-url',
      };
      const elementId = fieldMap[firstErrorKey];
      if (elementId) {
        const el = document.getElementById(elementId);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el?.focus();
      }
    }

    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    // Open blank popup synchronously for new OAuth servers to avoid popup
    // blocker. The popup is navigated to auth_url after the async OAuth init call.
    const willNeedOAuth =
      !editingConnection && teamspaceId && formData.credential_type === 'oauth';
    let oauthPopup: Window | null = null;
    if (willNeedOAuth) {
      oauthPopup = window.open('', '_blank', 'width=600,height=700');
      oauthPopupRef.current = oauthPopup;
      logOauthDebug('popup_opened_save_blank', {
        teamspaceId,
        popupTracked: Boolean(oauthPopup),
      });
    }

    saveInProgressRef.current = true;
    setIsSaving(true);
    try {
      if (editingConnection) {
        if (!teamspaceId) {
          showSnackbar(t('error-updating-mcp'), 'error');
          return;
        }
        const payload: MCPUpdatePayload = {
          name: formData.name,
          domain: formData.domain,
          url: formData.url,
          credential_type: formData.credential_type,
          oauth_client_id: formData.oauth_client_id.trim(),
        };
        if (formData.oauth_client_secret.trim()) {
          payload.oauth_client_secret = formData.oauth_client_secret;
        }
        await mcpDataService.adminUpdateServer(
          editingConnection.id,
          payload,
          teamspaceId,
        );
        fetchMCPConnections();
        showSnackbar(t('mcp-updated-successfully'), 'success');
      } else {
        const payload: MCPCreatePayload = {
          name: formData.name,
          domain: formData.domain,
          url: formData.url,
          credential_type: formData.credential_type,
        };
        if (teamspaceId) {
          payload.teamspace_id = teamspaceId;
        }
        if (formData.oauth_client_id.trim()) {
          payload.oauth_client_id = formData.oauth_client_id;
        }
        if (formData.oauth_client_secret.trim()) {
          payload.oauth_client_secret = formData.oauth_client_secret;
        }
        const response = await mcpDataService.adminCreateServer(payload);
        // Notify about auto-disabled if any
        if (response.auto_disabled.length > 0) {
          const names = response.auto_disabled.map((d) => d.name).join(', ');
          showSnackbar(
            `${t('mcp-created-successfully')}. ${t('mcp-auto-disabled')}: ${names}`,
            'success',
          );
        } else {
          showSnackbar(t('mcp-created-successfully'), 'success');
        }
        fetchMCPConnections();

        // Auto-trigger OAuth connect for new oauth-type servers.
        if (teamspaceId && response.created.credential_type === 'oauth') {
          const newServerId = response.created.id;
          oauthSuccessHandledRef.current = false;
          oauthPendingServerIdRef.current = newServerId;
          oauthPendingActionRef.current = 'save';
          try {
            const oauthResp = await mcpDataService.adminConnectOauth(
              newServerId,
              teamspaceId,
            );
            if (
              'auth_url' in oauthResp &&
              oauthResp.auth_url &&
              isSafeOAuthUrl(oauthResp.auth_url)
            ) {
              // Navigate the pre-opened blank popup to avoid popup blocker
              // Break taint chain: reconstruct from validated URL components (CWE-79)
              const parsedSaveUrl = new URL(oauthResp.auth_url);
              const sanitizedSaveUrl = `${parsedSaveUrl.origin}${parsedSaveUrl.pathname}${parsedSaveUrl.search}${parsedSaveUrl.hash}`;
              if (oauthPopup && !oauthPopup.closed) {
                if (!safeOAuthNavigate(oauthPopup, oauthResp.auth_url)) {
                  oauthPopup.close();
                  oauthPopupRef.current = null;
                  showSnackbar(
                    t('mcp-invalid-auth-url', 'Invalid authentication URL'),
                    'error',
                  );
                  return;
                }
              } else {
                oauthPopupRef.current = window.open(
                  sanitizedSaveUrl,
                  '_blank',
                  'noopener,noreferrer,width=600,height=700',
                );
              }
              logOauthDebug('popup_opened_save_auth', {
                pendingServerId: newServerId,
                teamspaceId,
                authUrl: sanitizedSaveUrl,
                popupTracked: Boolean(oauthPopupRef.current || oauthPopup),
              });
              showSnackbar(
                t('mcp-oauth-window-opened') ||
                  'OAuth window opened — complete authorization to connect',
                'success',
              );
            } else {
              // No auth_url returned — close the blank popup we pre-opened
              if (oauthPopup && !oauthPopup.closed) oauthPopup.close();
              oauthPopupRef.current = null;
            }
          } catch (oauthInitError: any) {
            if (oauthPopup && !oauthPopup.closed) oauthPopup.close();
            oauthPopupRef.current = null;
            oauthPendingServerIdRef.current = null;
            oauthPendingActionRef.current = null;
            fetchMCPConnections();
            const oauthMsg =
              oauthInitError?.data?.message ||
              oauthInitError?.data?.error ||
              tRef.current('mcp-oauth-connect-failed') ||
              'OAuth initiation failed';
            showSnackbar(oauthMsg, 'error');
          }
        } else {
          // Non-OAuth create or no teamspace — close pre-opened popup if any
          if (oauthPopup && !oauthPopup.closed) oauthPopup.close();
          oauthPopupRef.current = null;
        }
      }

      saveInProgressRef.current = false;
      setIsSaving(false);
      closePanel();
    } catch (error: any) {
      // Close pre-opened OAuth popup on any top-level error
      if (oauthPopup && !oauthPopup.closed) oauthPopup.close();
      oauthPopupRef.current = null;
      const raw =
        error?.data?.message ||
        error?.data?.error ||
        (editingConnection ? t('error-updating-mcp') : t('error-creating-mcp'));
      // Sanitize: strip HTML tags and cap length to prevent XSS via backend messages
      const msg = String(raw)
        .replace(/<[^<>]*>/g, '')
        .slice(0, 300);
      showSnackbar(msg, 'error');
      saveInProgressRef.current = false;
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!connectionToDelete) return;
    if (!teamspaceId) {
      showSnackbar(t('error-deleting-mcp'), 'error');
      return;
    }

    try {
      await mcpDataService.catalogCleanup(teamspaceId, connectionToDelete.id);
      await mcpDataService.adminDeleteServer(
        connectionToDelete.id,
        teamspaceId,
      );
      fetchMCPConnections();
      showSnackbar(
        `${connectionToDelete.name} ${t('deleted-successfully')}`,
        'success',
      );
    } catch (error: any) {
      const raw =
        error?.data?.message || error?.data?.error || t('error-deleting-mcp');
      // Sanitize: strip HTML tags and cap length to prevent XSS via backend messages
      const msg = String(raw)
        .replace(/<[^<>]*>/g, '')
        .slice(0, 300);
      showSnackbar(msg, 'error');
    } finally {
      setDeleteDialogOpen(false);
      setConnectionToDelete(null);
    }
  };

  const formatCredential = (type: string) => {
    if (type === 'internal') return 'Internal';
    if (type === 'oauth') return 'OAuth';
    return capitalize(type);
  };

  const inputClasses =
    'w-full bg-white dark:bg-carbon-darkPanel border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 placeholder-gray-400';

  const combinedConnections: CombinedMCPRow[] = [
    ...ootbServers.map((s) => ({
      key: `ootb-${s.id}`,
      is_ootb: true,
      is_enabled: s.is_enabled,
      is_locked:
        s.is_enabled &&
        LOCKED_OOTB_SERVER_NAMES.has(s.name.trim().toLowerCase()),
      name: capitalize(s.name),
      credential_type: 'Internal',
      domain: s.domain,
      url: s.url ?? '',
      ootb_server: s,
    })),
    ...connections.map((c) => ({
      key: c.id,
      is_ootb: false,
      is_enabled: c.is_enabled,
      is_locked: false,
      name: c.name,
      credential_type: formatCredential(c.credential_type),
      domain: c.domain,
      url: c.url ?? '',
      external_connection: c,
    })),
  ];

  return (
    <TooltipProvider>
      <div
        className="flex flex-col"
        id="id-mcp-tab-container"
        data-testid="mcp-tab-container"
      >
        <CustomSnackbar
          open={snackbarState.open}
          message={snackbarState.message}
          severity={snackbarState.severity}
          onClose={closeSnackbar}
          maxWidth={480}
        />

        {/* Header */}
        <div
          className="mb-6 flex items-center justify-between gap-4"
          id="id-mcp-tab-header"
          data-testid="mcp-tab-header"
        >
          <h2 className="text-base font-bold text-carbon-headingText dark:text-carbon-bodyTextDark">
            {t('manage-mcp', 'Manage MCP')}
          </h2>

          <Button
            onClick={openAddModal}
            className="rounded bg-brand-interactive text-white hover:bg-primary-hover dark:bg-brand-interactive dark:hover:bg-brand-blue"
          >
            {t('mcp-add-server', 'Add Server')}
          </Button>
        </div>

        {/* Unified MCP Table */}
        <div
          className="table-container"
          id="id-mcp-tab-table-container"
          data-testid="mcp-tab-table-container"
        >
          {isLoading ? (
            <div
              className="flex items-center justify-center py-8"
              id="id-mcp-tab-loading"
              data-testid="mcp-tab-loading"
            >
              <span
                className="text-gray-500 dark:text-gray-400"
                id="id-mcp-tab-loading-text"
                data-testid="mcp-tab-loading-text"
              >
                {t('loading-trans')}
              </span>
            </div>
          ) : (
            <Table>
              <TableHeader className="table-header">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="font-bold">{t('toggle')}</TableHead>
                  <TableHead className="font-bold">{t('name')}</TableHead>
                  <TableHead className="font-bold">
                    {t('credential-type')}
                  </TableHead>
                  <TableHead className="font-bold">{t('url')}</TableHead>
                  <TableHead className="font-bold">{t('domain')}</TableHead>
                  <TableHead className="font-bold">{t('status')}</TableHead>
                  <TableHead className="font-bold">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {combinedConnections.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center text-gray-500 dark:text-gray-400"
                    >
                      {t('no-mcp-connections', 'No MCP connections found')}
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {combinedConnections.map((row) => (
                      <TableRow
                        key={row.key}
                        className="table-row last:border-0"
                      >
                        <TableCell className="table-cell">
                          <div className="flex items-center gap-1.5">
                            <Switch
                              checked={row.is_enabled}
                              disabled={
                                row.is_locked ||
                                pendingEnables.has(
                                  row.is_ootb
                                    ? row.ootb_server!.id
                                    : row.external_connection!.id,
                                )
                              }
                              aria-label={
                                row.is_locked
                                  ? t(
                                      'mcp-loop-server-locked',
                                      'Loop Genie relies on this MCP server — it cannot be disabled.',
                                    )
                                  : undefined
                              }
                              onCheckedChange={() =>
                                row.is_ootb
                                  ? handleOotbToggle(row.ootb_server!)
                                  : handleToggle(row.external_connection!)
                              }
                            />
                            {row.is_locked && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    className="text-gray-400 dark:text-gray-500"
                                    data-testid={`mcp-tab-locked-icon-${row.key}`}
                                  >
                                    <Locked size={14} />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {t(
                                    'mcp-loop-server-locked',
                                    'Loop Genie relies on this MCP server — it cannot be disabled.',
                                  )}
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>

                        <TableCell className="table-cell text-xs">
                          {row.name}
                        </TableCell>

                        <TableCell className="table-cell text-xs">
                          {row.credential_type}
                        </TableCell>

                        <TableCell className="table-cell text-xs">
                          {row.url && (
                            <div
                              className="max-w-[220px]"
                              id={`id-mcp-tab-url-cell-${row.key}`}
                              data-testid="mcp-tab-url-cell"
                            >
                              <span className="truncate" title={row.url}>
                                {row.url}
                              </span>
                            </div>
                          )}
                        </TableCell>

                        <TableCell className="table-cell text-xs">
                          {row.domain?.toUpperCase()}
                        </TableCell>

                        <TableCell
                          className="table-cell text-xs"
                          id={`id-mcp-tab-status-cell-${row.key}`}
                          data-testid="mcp-tab-status-cell"
                        >
                          {(() => {
                            const rowServerId = row.is_ootb
                              ? row.ootb_server?.id
                              : row.external_connection?.id;
                            // A server can briefly have more than one job in
                            // ingestJobs (e.g. a prior failed job still
                            // inside its retention window while a new one
                            // runs) with no guaranteed backend ordering —
                            // filter out hidden/terminal jobs first, then
                            // prefer the newest by created_at so a stale
                            // hidden job can never mask an active one.
                            const job = ingestJobs
                              .filter(
                                (j) =>
                                  j.server_id === rowServerId &&
                                  !hiddenTerminalJobIds.has(j.job_id),
                              )
                              .sort((a, b) => b.created_at - a.created_at)[0];

                            if (!job) {
                              return (
                                <Badge
                                  data-testid={`mcp-tab-status-badge-${row.key}`}
                                  variant={
                                    row.is_enabled ? 'default' : 'destructive'
                                  }
                                  className={
                                    row.is_enabled
                                      ? 'border-transparent bg-status-successGreen text-white hover:bg-status-successGreen/80'
                                      : ''
                                  }
                                >
                                  {row.is_enabled
                                    ? t('online', 'Online')
                                    : t('offline', 'Offline')}
                                </Badge>
                              );
                            }

                            return (
                              <div
                                className="flex w-[140px] flex-col gap-1"
                                data-testid={`mcp-tab-status-job-${row.key}`}
                              >
                                <div className="flex items-center gap-1.5">
                                  {job.status === 'completed' && (
                                    <CheckmarkFilled
                                      size={14}
                                      className="shrink-0 text-status-successGreen"
                                    />
                                  )}
                                  {job.status === 'failed' && (
                                    <WarningAltFilled
                                      size={14}
                                      className="shrink-0 text-destructive"
                                    />
                                  )}
                                  <span
                                    className={`truncate ${
                                      job.status === 'failed'
                                        ? 'text-destructive'
                                        : 'text-gray-600 dark:text-gray-400'
                                    }`}
                                    title={
                                      job.status === 'failed' && job.error
                                        ? job.error
                                        : phaseLabel(job)
                                    }
                                  >
                                    {job.status === 'failed' && job.error
                                      ? job.error
                                      : phaseLabel(job)}
                                  </span>
                                </div>
                                <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                                  <div
                                    className={`h-full rounded-full transition-all duration-500 ease-out ${barColorClass(job)}`}
                                    style={{ width: barWidth(job) }}
                                  />
                                </div>
                              </div>
                            );
                          })()}
                        </TableCell>

                        <TableCell className="table-cell text-xs">
                          <div
                            className="flex items-center gap-3 text-gray-600 dark:text-gray-400"
                            id={`id-mcp-tab-actions-cell-${row.key}`}
                            data-testid="mcp-tab-actions-cell"
                          >
                            {/* Copy URL button - visible for all rows */}
                            {row.url && (
                              <button
                                onClick={() => handleCopyUrl(row.key, row.url)}
                                title={t('copy-mcp-url', 'Copy URL')}
                                aria-label={t('copy-mcp-url', 'Copy URL')}
                                data-testid={`mcp-copy-url-${row.key}`}
                                className={`${
                                  copiedKey === row.key
                                    ? 'text-green-600'
                                    : 'hover:text-blue-600 dark:hover:text-blue-400'
                                }`}
                              >
                                <Copy size={16} />
                              </button>
                            )}
                            {!row.is_ootb && row.external_connection && (
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={() =>
                                    openEditModal(row.external_connection!)
                                  }
                                  title="Edit"
                                  aria-label="Edit"
                                  className="hover:text-blue-600"
                                >
                                  <Edit size={16} />
                                </button>

                                {/* Admin OAuth Connect — visible only for external OAuth MCPs */}
                                {teamspaceId &&
                                  row.external_connection!.credential_type ===
                                    'oauth' && (
                                    <button
                                      onClick={() =>
                                        handleAdminConnectOauth(
                                          row.external_connection!,
                                        )
                                      }
                                      title="Connect OAuth"
                                      aria-label="Connect OAuth"
                                      disabled={
                                        isConnectingOauth ===
                                        row.external_connection!.id
                                      }
                                      className="hover:text-indigo-600 disabled:opacity-50"
                                    >
                                      <Checkmark size={16} />
                                    </button>
                                  )}

                                <button
                                  onClick={() => {
                                    setConnectionToDelete(
                                      row.external_connection!,
                                    );
                                    setDeleteDialogOpen(true);
                                  }}
                                  title="Delete"
                                  aria-label="Delete"
                                  className="hover:text-red-600"
                                >
                                  <TrashCan size={16} />
                                </button>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Add/Edit Side Panel */}
        {isPanelOpen && (
          <>
            {/* Backdrop */}
            <div
              role="presentation"
              id="id-mcp-tab-panel-backdrop"
              data-testid="mcp-tab-panel-backdrop"
              className="fixed inset-0 top-12 z-40 bg-black/20"
              onClick={closePanel}
              onKeyDown={(e) => {
                if (e.key === 'Escape') closePanel();
              }}
            />

            {/* Panel */}
            <div
              className="fixed bottom-0 right-0 top-12 z-50 flex w-[502px] flex-col border-l border-carbon-panelBorder bg-white shadow-xl dark:border-gray-700 dark:bg-carbon-darkPanel"
              id="id-mcp-tab-panel"
              data-testid="mcp-tab-panel"
            >
              {/* Header */}
              <div
                className="flex items-center justify-between border-b border-gray-200 bg-carbon-lightHeader p-4 dark:border-gray-700 dark:bg-carbon-darkHeader"
                id="id-mcp-tab-panel-header"
                data-testid="mcp-tab-panel-header"
              >
                <h2 className="text-base font-semibold text-gray-700 dark:text-gray-300">
                  {editingConnection
                    ? t('edit-mcp-connection')
                    : t('mcp-add-server', 'Add Server')}
                </h2>
                <button
                  onClick={closePanel}
                  className="rounded p-1 transition-colors hover:bg-gray-300 dark:hover:bg-gray-600"
                  disabled={saveInProgressRef.current}
                  aria-label={t('Close')}
                >
                  <Close
                    size={16}
                    className="text-gray-600 dark:text-gray-400"
                  />
                </button>
              </div>

              {/* Content */}
              <div
                className="flex-1 overflow-y-auto p-6"
                id="id-mcp-tab-panel-content"
                data-testid="mcp-tab-panel-content"
              >
                <form className="space-y-6">
                  {/* Name */}
                  <div
                    className="space-y-2"
                    id="id-mcp-tab-name-field"
                    data-testid="mcp-tab-name-field"
                  >
                    <Label
                      htmlFor="mcp-name"
                      className="text-gray-700 dark:text-gray-300"
                    >
                      {t('name')}{' '}
                      <span
                        className="text-red-500"
                        id="id-mcp-tab-name-required"
                        data-testid="mcp-tab-name-required"
                      >
                        *
                      </span>
                    </Label>
                    <Input
                      id="mcp-name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => {
                        const input = e.target.value;
                        if (input !== '' && !/^[a-zA-Z0-9 _-]*$/.test(input)) {
                          setFormErrors((prev) => ({
                            ...prev,
                            name: t('specialCharactersNotAllowed'),
                          }));
                          return;
                        }
                        setFormData({ ...formData, name: input });
                        setFormErrors((prev) => ({ ...prev, name: undefined }));
                      }}
                      placeholder={t('enter-name')}
                      className={cn(
                        inputClasses,
                        formErrors.name &&
                          'border-red-500 focus-visible:ring-red-500',
                      )}
                    />
                    {formErrors.name && (
                      <p className="text-xs text-red-500">{formErrors.name}</p>
                    )}
                  </div>

                  {/* Domain */}
                  <div
                    className="space-y-2"
                    id="id-mcp-tab-domain-field"
                    data-testid="mcp-tab-domain-field"
                  >
                    <Label
                      htmlFor="mcp-domain"
                      className="text-gray-700 dark:text-gray-300"
                    >
                      {t('domain')}{' '}
                      <span
                        className="text-red-500"
                        id="id-mcp-tab-domain-required"
                        data-testid="mcp-tab-domain-required"
                      >
                        *
                      </span>
                    </Label>
                    <Select
                      value={formData.domain}
                      onValueChange={(value: string) => {
                        setFormData({ ...formData, domain: value });
                        setFormErrors((prev) => ({
                          ...prev,
                          domain: undefined,
                        }));
                      }}
                    >
                      <SelectTrigger
                        id="mcp-domain"
                        className={cn(
                          'w-full bg-white dark:bg-carbon-darkPanel',
                          formErrors.domain && 'border-red-500',
                        )}
                      >
                        <SelectValue placeholder={t('select-domain')} />
                      </SelectTrigger>
                      <SelectContent>
                        {DOMAIN_OPTIONS.map((d) => (
                          <SelectItem key={d} value={d}>
                            {d}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formErrors.domain && (
                      <p className="text-xs text-red-500">
                        {formErrors.domain}
                      </p>
                    )}
                  </div>

                  {/* Server URL */}
                  <div
                    className="space-y-2"
                    id="id-mcp-tab-url-field"
                    data-testid="mcp-tab-url-field"
                  >
                    <Label
                      htmlFor="mcp-url"
                      className="text-gray-700 dark:text-gray-300"
                    >
                      {t('mcp-server-url')}{' '}
                      <span
                        className="text-red-500"
                        id="id-mcp-tab-url-required"
                        data-testid="mcp-tab-url-required"
                      >
                        *
                      </span>
                    </Label>
                    <Input
                      id="mcp-url"
                      type="text"
                      value={formData.url}
                      onChange={(e) => {
                        setFormData({ ...formData, url: e.target.value });
                        setFormErrors((prev) => ({ ...prev, url: undefined }));
                      }}
                      placeholder={t('mcp-enter-server-url')}
                      className={cn(
                        inputClasses,
                        formErrors.url &&
                          'border-red-500 focus-visible:ring-red-500',
                      )}
                    />
                    {formErrors.url && (
                      <p className="text-xs text-red-500">{formErrors.url}</p>
                    )}
                  </div>

                  {/* Credential Type */}
                  <div
                    className="space-y-2"
                    id="id-mcp-tab-credential-type-field"
                    data-testid="mcp-tab-credential-type-field"
                  >
                    <Label
                      htmlFor="mcp-credential-type"
                      className="text-gray-700 dark:text-gray-300"
                    >
                      {t('credential-type')}{' '}
                      <span
                        className="text-red-500"
                        id="id-mcp-tab-credential-type-required"
                        data-testid="mcp-tab-credential-type-required"
                      >
                        *
                      </span>
                    </Label>
                    <Select
                      value={formData.credential_type}
                      onValueChange={(value: string) => {
                        setFormData({ ...formData, credential_type: value });
                      }}
                    >
                      <SelectTrigger
                        id="mcp-credential-type"
                        className="w-full bg-white dark:bg-carbon-darkPanel"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CREDENTIAL_TYPE_OPTIONS.map((ct) => (
                          <SelectItem key={ct} value={ct}>
                            {ct}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Client ID (optional) */}
                  <div
                    className="space-y-2"
                    id="id-mcp-tab-client-id-field"
                    data-testid="mcp-tab-client-id-field"
                  >
                    <Label
                      htmlFor="mcp-oauth-client-id"
                      className="text-gray-700 dark:text-gray-300"
                    >
                      {t('mcp-client-id')}
                    </Label>
                    <Input
                      id="mcp-oauth-client-id"
                      type="text"
                      value={formData.oauth_client_id}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          oauth_client_id: e.target.value,
                        });
                      }}
                      placeholder={t('mcp-enter-client-id')}
                      className={inputClasses}
                    />
                  </div>

                  {/* Client Secret (optional) */}
                  <div
                    className="space-y-2"
                    id="id-mcp-tab-client-secret-field"
                    data-testid="mcp-tab-client-secret-field"
                  >
                    <Label
                      htmlFor="mcp-oauth-client-secret"
                      className="text-gray-700 dark:text-gray-300"
                    >
                      {t('mcp-client-secret')}
                    </Label>
                    <Input
                      id="mcp-oauth-client-secret"
                      type="password"
                      value={formData.oauth_client_secret}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          oauth_client_secret: e.target.value,
                        });
                      }}
                      placeholder={
                        editingConnection
                          ? t('leave-blank-to-keep-existing')
                          : t('mcp-enter-client-secret')
                      }
                      className={inputClasses}
                    />
                  </div>
                </form>
              </div>

              {/* Footer */}
              <div
                className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700"
                id="id-mcp-tab-panel-footer"
                data-testid="mcp-tab-panel-footer"
              >
                <Button
                  onClick={closePanel}
                  variant="outline"
                  disabled={saveInProgressRef.current}
                  className="w-[100px] border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  {t('cancel')}
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="w-[100px] rounded bg-brand-interactive text-white hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50 dark:bg-brand-interactive dark:hover:bg-brand-blue"
                >
                  {isSaving
                    ? t('saving')
                    : editingConnection
                      ? t('update')
                      : t('save')}
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="max-w-md pt-6">
            <div
              className="flex items-start gap-4"
              id="id-mcp-tab-delete-dialog-content"
              data-testid="mcp-tab-delete-dialog-content"
            >
              <WarningAlt size={20} className="mt-1 text-red-600" />
              <div
                id="id-mcp-tab-delete-dialog-text"
                data-testid="mcp-tab-delete-dialog-text"
              >
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {t('confirm-deletion', 'Confirm Deletion')}
                </h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {t(
                    'are-you-sure-delete-mcp',
                    `Are you sure you want to delete "${connectionToDelete?.name}"?`,
                  )}
                </p>
              </div>
            </div>
            <div
              className="mt-6 flex justify-end gap-2"
              id="id-mcp-tab-delete-dialog-actions"
              data-testid="mcp-tab-delete-dialog-actions"
            >
              <Button
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setConnectionToDelete(null);
                }}
                variant="outline"
              >
                {t('cancel', 'Cancel')}
              </Button>
              <Button
                onClick={handleDelete}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                {t('yes-delete')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}

export default MCPTab;
